import React, { useState, useMemo } from "react";
import {
  Box,
  VStack,
  HStack,
  Card,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  Text,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Textarea,
  Select,
  Input,
  useToast,
  Spinner,
  Flex,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { 
  FiCheck, 
  FiX, 
  FiEye, 
  FiFilter, 
  FiRefreshCw,
  FiSearch,
  FiDownload
} from "react-icons/fi";
import { 
  useOvertimeRequests, 
  useApproveOvertimeRequest,
  overtimeManagementUtils 
} from "../../../services/adminOvertimeService";

const OvertimeRequests = () => {
  const [filters, setFilters] = useState({
    status: 'all',
    employee_id: '',
    date_from: '',
    date_to: ''
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approvalData, setApprovalData] = useState({
    status: 'approved',
    comments: ''
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Load overtime requests
  const {
    requestsData,
    requestsError,
    requestsIsLoading,
    refetchRequests
  } = useOvertimeRequests({ filters });

  // Approve/reject request
  const {
    approveRequest,
    approveRequestIsLoading,
    approveRequestError
  } = useApproveOvertimeRequest(() => {
    toast({
      title: "Request Updated",
      description: `Overtime request has been ${approvalData.status}.`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    onClose();
    refetchRequests();
    setSelectedRequest(null);
    setApprovalData({ status: 'approved', comments: '' });
  });

  // Filter requests based on search term
  const filteredRequests = useMemo(() => {
    if (!requestsData?.length) return [];
    
    return requestsData.filter(request => 
      request.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.employee_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.reason?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [requestsData, searchTerm]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApproval = () => {
    if (!selectedRequest) return;
    
    approveRequest({
      requestId: selectedRequest.request_id,
      payload: approvalData
    });
  };

  const openApprovalModal = (request) => {
    setSelectedRequest(request);
    setApprovalData({ status: 'approved', comments: '' });
    onOpen();
  };

  const getStatusBadge = (status) => {
    const colorScheme = overtimeManagementUtils.getRequestStatusColor(status);
    const displayName = overtimeManagementUtils.getRequestStatusDisplay(status);
    
    return (
      <Badge colorScheme={colorScheme} variant="subtle" size="sm">
        {displayName}
      </Badge>
    );
  };

  if (requestsIsLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4}>Loading overtime requests...</Text>
      </Box>
    );
  }

  if (requestsError) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertTitle>Error Loading Requests</AlertTitle>
        <AlertDescription>
          {requestsError.message || "Failed to load overtime requests. Please try again."}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Error Alert */}
      {approveRequestError && (
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Update Failed</AlertTitle>
          <AlertDescription>
            {approveRequestError.message || "Failed to update request. Please try again."}
          </AlertDescription>
        </Alert>
      )}

      {/* Filters and Actions */}
      <Card>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Flex justify="space-between" align="center">
              <Text fontSize="lg" fontWeight="600" color="gray.800">
                Overtime Requests
              </Text>
              <HStack spacing={2}>
                <Button
                  leftIcon={<FiRefreshCw />}
                  variant="outline"
                  size="sm"
                  onClick={refetchRequests}
                  isLoading={requestsIsLoading}
                >
                  Refresh
                </Button>
                <Button
                  leftIcon={<FiDownload />}
                  variant="outline"
                  size="sm"
                >
                  Export
                </Button>
              </HStack>
            </Flex>

            <HStack spacing={4} wrap="wrap">
              {/* Search */}
              <Box position="relative" minW="300px">
                <Input
                  placeholder="Search employees or reasons..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  pl={10}
                  pr={4}
                />
                <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" color="gray.400">
                  <FiSearch size={16} />
                </Box>
              </Box>

              {/* Status Filter */}
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                minW="150px"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </Select>

              {/* Date Filters */}
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                placeholder="From Date"
                minW="150px"
              />
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                placeholder="To Date"
                minW="150px"
              />
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Requests Table */}
      <Card>
        <CardBody>
          {filteredRequests.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Text fontSize="lg" color="gray.500">
                No overtime requests found
              </Text>
              <Text fontSize="sm" color="gray.400" mt={2}>
                {searchTerm || Object.values(filters).some(f => f && f !== 'all') 
                  ? "Try adjusting your search or filters" 
                  : "No requests have been submitted yet"}
              </Text>
            </Box>
          ) : (
            <TableContainer>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Employee</Th>
                    <Th>Date</Th>
                    <Th>Hours Requested</Th>
                    <Th>Reason</Th>
                    <Th>Status</Th>
                    <Th>Submitted</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredRequests.map((request) => (
                    <Tr key={request.request_id}>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="600">{request.employee_name}</Text>
                          <Text fontSize="sm" color="gray.500">{request.employee_id}</Text>
                        </VStack>
                      </Td>
                      <Td>
                        <Text>{new Date(request.date).toLocaleDateString()}</Text>
                      </Td>
                      <Td>
                        <Text fontWeight="600" color="blue.600">
                          {overtimeManagementUtils.formatOvertimeHours(request.hours_requested)}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm" noOfLines={2}>
                          {request.reason || "No reason provided"}
                        </Text>
                      </Td>
                      <Td>
                        {getStatusBadge(request.status)}
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.500">
                          {new Date(request.created_at).toLocaleDateString()}
                        </Text>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          {request.status === 'pending' && (
                            <>
                              <Tooltip label="Approve Request">
                                <IconButton
                                  icon={<FiCheck />}
                                  colorScheme="green"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openApprovalModal(request)}
                                />
                              </Tooltip>
                              <Tooltip label="Reject Request">
                                <IconButton
                                  icon={<FiX />}
                                  colorScheme="red"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setApprovalData({ status: 'rejected', comments: '' });
                                    openApprovalModal(request);
                                  }}
                                />
                              </Tooltip>
                            </>
                          )}
                          <Tooltip label="View Details">
                            <IconButton
                              icon={<FiEye />}
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedRequest(request);
                                onOpen();
                              }}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </CardBody>
      </Card>

      {/* Approval Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedRequest?.status === 'pending' ? 'Approve/Reject Request' : 'Request Details'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedRequest && (
              <VStack spacing={4} align="stretch">
                {/* Request Details */}
                <Box>
                  <Text fontWeight="600" mb={2}>Request Details</Text>
                  <VStack spacing={2} align="stretch" fontSize="sm">
                    <HStack justify="space-between">
                      <Text color="gray.600">Employee:</Text>
                      <Text fontWeight="500">{selectedRequest.employee_name}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Date:</Text>
                      <Text>{new Date(selectedRequest.date).toLocaleDateString()}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Hours Requested:</Text>
                      <Text fontWeight="500" color="blue.600">
                        {overtimeManagementUtils.formatOvertimeHours(selectedRequest.hours_requested)}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Status:</Text>
                      {getStatusBadge(selectedRequest.status)}
                    </HStack>
                  </VStack>
                </Box>

                <Box>
                  <Text fontWeight="600" mb={2}>Reason</Text>
                  <Text fontSize="sm" p={3} bg="gray.50" borderRadius="md">
                    {selectedRequest.reason || "No reason provided"}
                  </Text>
                </Box>

                {/* Approval Form (only for pending requests) */}
                {selectedRequest.status === 'pending' && (
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <FormLabel>Action</FormLabel>
                      <Select
                        value={approvalData.status}
                        onChange={(e) => setApprovalData(prev => ({ ...prev, status: e.target.value }))}
                      >
                        <option value="approved">Approve</option>
                        <option value="rejected">Reject</option>
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Comments (Optional)</FormLabel>
                      <Textarea
                        value={approvalData.comments}
                        onChange={(e) => setApprovalData(prev => ({ ...prev, comments: e.target.value }))}
                        placeholder="Add any comments or notes..."
                        rows={3}
                      />
                    </FormControl>
                  </VStack>
                )}
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              {selectedRequest?.status === 'pending' && (
                <Button
                  colorScheme={approvalData.status === 'approved' ? 'green' : 'red'}
                  onClick={handleApproval}
                  isLoading={approveRequestIsLoading}
                  loadingText="Updating..."
                >
                  {approvalData.status === 'approved' ? 'Approve' : 'Reject'}
                </Button>
              )}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default OvertimeRequests; 